import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AnimationEvent } from '@angular/animations';
import { ToastService } from '../../services/toast.service';
import type { ToastInstance, ToastSeverity } from '../../services/toast.types';
import { toastItemSlideTrigger } from './toast-item.animations';

@Component({
  selector: 'app-toast-host',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './toast-host.component.html',
  styleUrl: './toast-host.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [toastItemSlideTrigger],
})
export class ToastHostComponent {
  protected readonly toastService = inject(ToastService);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor() {
    effect(() => {
      this.toastService.toasts();
      this.cdr.markForCheck();
    });
  }

  protected itemClass(severity: ToastSeverity): string {
    return `toast-host__item toast-host__item--${severity}`;
  }

  protected toastA11y(severity: ToastSeverity): {
    role: 'alert' | 'status';
    live: 'assertive' | 'polite';
  } {
    if (severity === 'error' || severity === 'warning') {
      return { role: 'alert', live: 'assertive' };
    }
    return { role: 'status', live: 'polite' };
  }

  protected dismiss(id: string): void {
    this.toastService.dismiss(id);
  }

  protected onCloseToast(event: Event, id: string): void {
    event.preventDefault();
    this.dismiss(id);
  }

  protected onCloseToastKeydown(event: Event, id: string): void {
    const keyEvent = event as KeyboardEvent;
    if (keyEvent.key !== ' ' && keyEvent.key !== 'Spacebar') {
      return;
    }
    keyEvent.preventDefault();
    this.dismiss(id);
  }

  protected onToastAnimationDone(event: AnimationEvent, toast: ToastInstance): void {
    if (event.phaseName !== 'done' || event.toState !== 'out') {
      return;
    }
    this.toastService.removeAfterExit(toast.id);
  }
}
